import React, { useState, useCallback } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCopy, faEye } from "@fortawesome/free-solid-svg-icons";
import fetch_backend from "../../../App/components/shared/Fetch";
import { styles_react_select } from "../../../App/components/shared/Select";
import { url_bill_of_materials } from "../../constants/constants";

function ProductsTab({ token, dispatch, formatCurrency, duplicateFromProduct }) {
  const [filterOptions, setFilterOptions] = useState({ products: [], sizes: [] });
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterProduct, setFilterProduct] = useState(null);
  const [filterSize, setFilterSize] = useState(null);

  const loadFilters = useCallback(async () => {
    if (filtersLoaded) return;
    setSearching(true);
    try {
      const response = await fetch_backend("product_quote_price/history/options/", "GET", token, dispatch);
      if (response && response.status === 200) {
        setFilterOptions({
          // El tamaño va en el label porque hay productos con el mismo nombre y
          // distinto tamaño (ej: Oficina Tipo C en 20 Ft y en 40 Ft HC), y son
          // hojas de costos diferentes.
          products: (response.products || []).map(p => ({
            value: p.id,
            label: p.size ? `${p.name} — ${p.size}` : p.name,
          })),
          sizes: (response.sizes || []).map(s => ({ value: s.id, label: s.name })),
        });
        setFiltersLoaded(true);
      }
    } catch (error) {
      console.error("Failed to fetch product history filter options", error);
    } finally {
      setSearching(false);
    }
  }, [filtersLoaded, token, dispatch]);

  const searchProducts = async () => {
    setSearching(true);
    const params = new URLSearchParams();
    if (filterProduct) params.append("product_id", filterProduct.value);
    if (filterSize) params.append("size_id", filterSize.value);

    try {
      const response = await fetch_backend(`product_quote_price/history/search/?${params.toString()}`, "GET", token, dispatch);
      if (response && response.status === 200) {
        setResults(response.results || []);
        setHasSearched(true);
      }
    } catch (error) {
      console.error("Failed to search product cost sheets", error);
    } finally {
      setSearching(false);
    }
  };

  const viewProductDetail = (productId) => {
    // Se abre en pestaña nueva para no perder la hoja de costos en curso.
    const url = `${url_bill_of_materials}/detail?bom_id=${productId}&is_product=1`;
    window.open(url, '_blank');
  };

  const formatTrm = (value) => {
    const parsed = parseFloat(value);
    if (!parsed) return "-";
    return parsed.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Load filters on first render
  React.useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  return (
    <div className="history-container">
      <div className="history-search-panel">
        <h3 className="history-search-title">
          <FontAwesomeIcon icon={faSearch} />
          <span>Buscar Hojas de Costos de Productos</span>
        </h3>
        <div className="history-filters">
          <div className="history-filter-row">
            <div className="history-filter-field">
              <label className="history-filter-label">Producto</label>
              <Select
                placeholder="Todos los productos"
                options={filterOptions.products}
                styles={styles_react_select}
                value={filterProduct}
                onChange={setFilterProduct}
                isClearable
              />
            </div>
            <div className="history-filter-field">
              <label className="history-filter-label">Tamaño</label>
              <Select
                placeholder="Todos los tamaños"
                options={filterOptions.sizes}
                styles={styles_react_select}
                value={filterSize}
                onChange={setFilterSize}
                isClearable
              />
            </div>
          </div>
          <div className="history-search-actions">
            <button className="btn-history-search" onClick={searchProducts} disabled={searching}>
              <FontAwesomeIcon icon={faSearch} />
              <span>{searching ? "Buscando..." : "Buscar"}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="history-results-panel">
        {searching ? (
          <div className="history-results-loading">Buscando resultados...</div>
        ) : results.length > 0 ? (
          <>
            <div className="history-results-header">
              <span>{results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}</span>
            </div>
            <table className="history-results-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Tamaño</th>
                  <th>Descripción</th>
                  <th>TRM</th>
                  <th>Total</th>
                  <th>Ítems</th>
                  <th style={{ textAlign: "center" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result) => (
                  <tr key={result.id}>
                    <td>{result.product_name}</td>
                    <td>{result.size || "-"}</td>
                    <td className="history-desc-cell">{result.description || "-"}</td>
                    <td>{formatTrm(result.trm_value)}</td>
                    <td className="history-total-cell">{formatCurrency(result.total || 0)}</td>
                    <td>{result.items_count ?? 0}</td>
                    <td style={{ textAlign: "center" }}>
                      <div className="history-actions-cell">
                        <button className="btn-history-load" onClick={() => duplicateFromProduct(result.id)}>
                          <FontAwesomeIcon icon={faCopy} />
                          <span>Duplicar</span>
                        </button>
                        <button className="btn-history-view" onClick={() => viewProductDetail(result.id)}>
                          <FontAwesomeIcon icon={faEye} />
                          <span>Ver detalle</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="history-empty-results">
            <FontAwesomeIcon icon={faSearch} size="2x" color="#ccc" />
            <p>
              {hasSearched
                ? "No se encontraron hojas de costos de productos con esos filtros."
                : "Usa los filtros para buscar hojas de costos de productos."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductsTab;
